import React, { useCallback, useEffect, useRef, useState, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import secureLocalStorageModule from 'react-secure-storage';

const secureLocalStorage = (secureLocalStorageModule as unknown as {default?: typeof secureLocalStorageModule}).default ?? secureLocalStorageModule;
import ApiConstants from '../../../../core/constants/api_constants';
import { VibeWebsocketContext, VibeWebsocketContextType, VibeWebsocketListener } from './vibe_websocket_context';

type PendingEmit = { event: string; args: unknown[] };

interface VibeWebsocketProviderProps {
  children: ReactNode;
}

export const VibeWebsocketProvider: React.FC<VibeWebsocketProviderProps> = ({ children }) => {
  const socketRef = useRef<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const pendingEmitsRef = useRef<PendingEmit[]>([]);

  const flushPendingEmits = useCallback((s: Socket) => {
    if (pendingEmitsRef.current.length === 0) return;
    const queue = pendingEmitsRef.current;
    pendingEmitsRef.current = [];
    queue.forEach(({ event, args }) => {
      s.emit(event, ...args);
    });
  }, []);

  const connect = useCallback(() => {
    const existing = socketRef.current;
    if (existing && existing.connected) {
      return;
    }
    if (existing && !existing.connected) {
      existing.disconnect();
      socketRef.current = null;
    }

    const socketUrl = ApiConstants().apiBaseUrl;
    const token = secureLocalStorage.getItem("accessToken") as string | null;

    const newSocket = io(socketUrl, {
      transports: ['websocket'],
      auth: { token },
      withCredentials: false,
    });

    newSocket.on('connect', () => {
      setConnected(true);
      flushPendingEmits(newSocket);
    });

    newSocket.on('disconnect', () => {
      setConnected(false);
    });

    socketRef.current = newSocket;
  }, [flushPendingEmits]);

  const disconnect = useCallback(() => {
    const current = socketRef.current;
    if (current) {
      current.disconnect();
      socketRef.current = null;
      setConnected(false);
    }
  }, []);

  const on = useCallback(<A extends unknown[]>(event: string, callback: VibeWebsocketListener<A>) => {
    socketRef.current?.on(event, callback);
  }, []);

  const off = useCallback(<A extends unknown[]>(event: string, callback?: VibeWebsocketListener<A>) => {
    socketRef.current?.off(event, callback);
  }, []);

  const emit = useCallback((event: string, ...args: unknown[]) => {
    const current = socketRef.current;
    if (current && current.connected) {
      current.emit(event, ...args);
      return;
    }
    pendingEmitsRef.current.push({ event, args });
    connect();
  }, [connect]);

  useEffect(() => {
    connect();
    return () => {
      const current = socketRef.current;
      if (current) {
        current.disconnect();
        socketRef.current = null;
      }
    };
  }, [connect]);

  const value: VibeWebsocketContextType = {
    connected,
    on,
    off,
    emit,
    connect,
    disconnect,
  };

  return (
    <VibeWebsocketContext.Provider value={value}>
      {children}
    </VibeWebsocketContext.Provider>
  );
};
